import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/admin-responsive.css';
import WarehouseManagement from '../components/admin/WarehouseManagement';
import TaskGroupsManagement from '../components/admin/TaskGroupsManagement';
import {
  SystemTab, CompanyTab, ManufacturersTab, CategoriesTab,
  LocationsTab, FiscalPrinterTab, GusSettings, KSeFSettings, MessagesTab,
  DiscountsTab, DiscountReportsTab, DailyClosureTab,
  AutoBackupTab, SettingsTab, SalesTargetsTab,
  LogsTab, UnitsTab, VatRatesTab, QuickProductsTab, TrustedDevicesTab
} from '../components/admin';
import PermissionsTab from '../components/admin/tabs/PermissionsTab';
import ChangelogAdmin from '../components/admin/tabs/ChangelogAdmin';
import CorrectionsTab from '../components/admin/tabs/CorrectionsTab';
import GrafikTab from '../components/admin/tabs/GrafikTab';
import RevenueReportTab from '../components/admin/tabs/RevenueReportTab';
import SalesByHourTab from '../components/admin/tabs/SalesByHourTab';
import SimpleTemplateEditor from '../components/admin/SimpleTemplateEditor';
import SmsApiSettings from '../components/admin/SmsApiSettings';
import AnnouncementAdmin from '../components/announcements/AnnouncementAdmin';
import DocumentPrefixesPage from './DocumentPrefixesPage';
import DepositManager from '../components/admin/DepositManager';
import PriceLimitsTab from '../components/admin/PriceLimitsTab';
import InventoryReportPage from './InventoryReportPage';
import BonusPage from './BonusPage';
import ReklamacjeEmailTab from '../components/admin/tabs/ReklamacjeEmailTab';
import KomunikatorTab from '../components/admin/tabs/KomunikatorTab';
import SfdB2BSettings from '../components/admin/SfdB2BSettings';
import KontrolaFakturPage from './KontrolaFakturPage';
import KontrolaKorektPage from './KontrolaKorektPage';
import SfdZamowieniaPage from './SfdZamowieniaPage';
import KsefOtrzymanePage from './KsefOtrzymanePage';

const AdminPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?tab=... pozwala linkowac prosto w zakladke (np. z Kontroli faktur SFD do 'sfd-b2b')
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'system');
  const [error, setError] = useState(null);

  // Definicja wierszy nawigacji
  const tabRows = [
    {
      label: 'System',
      icon: '🖥️',
      bg: 'var(--bg)',
      tabs: [
        { id: 'system', icon: '📊', label: 'Info' },
        { id: 'settings', icon: '⚙️', label: 'Ustawienia' },
        { id: 'fiscal-printer', icon: '🖨️', label: 'Drukarka' },
        { id: 'quick-products', icon: '🛒', label: 'Szybkie Produkty' },
        { id: 'auto-backup', icon: '💾', label: 'Backup' },
        { id: 'logs', icon: '📋', label: 'Logi' },
        { id: 'sms-api', icon: '📱', label: 'SMS API' },
        { id: 'gus', icon: '🏛️', label: 'GUS/VAT' },
        { id: 'ksef', icon: '📄', label: 'KSeF' },
        { id: 'sfd-b2b', icon: '🧾', label: 'SFD B2B' },
        { id: 'kontrola-faktur', icon: '✅', label: 'Kontrola faktur SFD' },
        { id: 'kontrola-korekt', icon: '↩️', label: 'Kontrola korekt SFD' },
        { id: 'sfd-zamowienia', icon: '📦', label: 'Zamówienia z B2B' },
        { id: 'ksef-otrzymane', icon: '🧾', label: 'Faktury z KSeF' },
        { id: 'changelog', icon: '📢', label: 'Changelog' }
      ]
    },
    {
      label: 'Firma',
      icon: '🏢',
      bg: 'var(--card-bg)' ,
      tabs: [
        { id: 'company', icon: '📝', label: 'Dane Firmy' },
        { id: 'locations', icon: '📍', label: 'Lokalizacje' },
        { id: 'permissions', icon: '🔐', label: 'Uprawnienia' },
        { id: 'trusted-devices', icon: '🛡️', label: 'Urządzenia' },
        { id: 'warehouses', icon: '🏪', label: 'Magazyny' },
        { id: 'sales-targets', icon: '🎯', label: 'Cele Sprzedaży' },
        { id: 'bonus-config', icon: '🏆', label: 'Premie' },
        { id: 'announcements', icon: '📢', label: 'Ogłoszenia' },
        { id: 'messages', icon: '✉️', label: 'Wiadomości' },
        { id: 'komunikator', icon: '💬', label: 'Komunikator' },
        { id: 'reklamacje-email', icon: '📧', label: 'Maile reklamacji' }
      ]
    },
    {
      label: 'Produkty',
      icon: '🛍️',
      bg: 'var(--card-bg)' ,
      tabs: [
        { id: 'categories', icon: '🏷️', label: 'Kategorie' },
        { id: 'manufacturers', icon: '🏭', label: 'Producenci' },
        { id: 'units', icon: '📏', label: 'Jednostki miary' },
        { id: 'vat', icon: '💰', label: 'Stawki VAT' },
        { id: 'deposits', icon: '🔄', label: 'Kaucje' },
        { id: 'discounts', icon: '🎯', label: 'Rabaty' },
        { id: 'price-limits', icon: '🚫', label: 'Granice Ceny' },
        { id: 'discount-reports', icon: '📊', label: 'Raporty' },
        { id: 'inventory-report', icon: '📦', label: 'Raport Magazynowy' }
      ]
    },
    {
      label: 'Dokumenty',
      icon: '📄',
      bg: 'var(--bg)',
      tabs: [
        { id: 'template-editor', icon: '✏️', label: 'Szablony' },
        { id: 'document-prefixes', icon: '🏷️', label: 'Prefiksy' },
        { id: 'daily-closure', icon: '🔒', label: 'Zamknięcie' },
        { id: 'corrections', icon: '📋', label: 'Korekty' },
        { id: 'revenue-report', icon: '📈', label: 'Obrót / Marża' },
        { id: 'sales-by-hour', icon: '📊', label: 'Sprzedaż wg godzin' },
        { id: 'task-groups', icon: '📋', label: 'Grupy zadań' }
      ]
    },
    {
      label: 'Personel',
      icon: '👥',
      bg: 'var(--card-bg)',
      tabs: [
        { id: 'grafik', icon: '📅', label: 'Grafik' }
      ]
    }
  ];

  // Mapowanie tab ID -> komponent
  const renderTabContent = () => {
    switch (activeTab) {
      case 'system': return <SystemTab />;
      case 'company': return <CompanyTab />;
      case 'announcements': return <AnnouncementAdmin />;
      case 'fiscal-printer': return <FiscalPrinterTab />;
      case 'quick-products': return <QuickProductsTab />;
      case 'template-editor': return <SimpleTemplateEditor />;
      case 'manufacturers': return <ManufacturersTab />;
      case 'units': return <UnitsTab />;
      case 'vat': return <VatRatesTab />;
      case 'warehouses': return <WarehouseManagement />;
      case 'locations': return <LocationsTab />;
      case 'permissions': return <PermissionsTab />;
      case 'trusted-devices': return <TrustedDevicesTab />;
      case 'reklamacje-email': return <ReklamacjeEmailTab />;
      case 'categories': return <CategoriesTab />;
      case 'deposits': return <DepositManager />;
      case 'discounts': return <DiscountsTab />;
      case 'price-limits': return <PriceLimitsTab locationId={5} />;
      case 'discount-reports': return <DiscountReportsTab />;
      case 'inventory-report': return <InventoryReportPage />;
      case 'daily-closure': return <DailyClosureTab />;
      case 'corrections': return <CorrectionsTab />;
      case 'revenue-report': return <RevenueReportTab />;
      case 'sales-by-hour': return <SalesByHourTab />;
      case 'document-prefixes': return <DocumentPrefixesPage />;
      case 'auto-backup': return <AutoBackupTab />;
      case 'settings': return <SettingsTab />;
      case 'sales-targets': return <SalesTargetsTab />;
      case 'logs': return <LogsTab />;
      case 'sms-api': return <SmsApiSettings />;
      case 'gus': return <GusSettings />;
      case 'ksef': return <KSeFSettings />;
      case 'sfd-b2b': return <SfdB2BSettings />;
      // Decyzja wlasciciela (04.09): kontrola faktur na razie tylko w panelu admina
      case 'kontrola-faktur': return <KontrolaFakturPage />;
      case 'kontrola-korekt': return <KontrolaKorektPage />;
      // Zamowienia zlozone przez sklepy na b2b.sfd.pl - tylko podglad, tylko panel admina
      case 'sfd-zamowienia': return <SfdZamowieniaPage />;
      // Faktury zakupu pobrane z KSeF (nabywca) - tylko panel admina
      case 'ksef-otrzymane': return <KsefOtrzymanePage />;
      case 'changelog': return <ChangelogAdmin />;
      case 'task-groups': return <TaskGroupsManagement />;
      case 'messages': return <MessagesTab />;
      case 'komunikator': return <KomunikatorTab />;
      case 'bonus-config': return <BonusPage embedded={true} />;
      case 'grafik': return <GrafikTab />;
      default: return <SystemTab />;
    }
  };

  return (
    <div className="admin-page-wrapper" style={{
      padding: '1.5rem'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '1.5rem',
        backgroundColor: 'var(--card-bg)',
        padding: '1.5rem',
        borderRadius: '0.375rem',
        border: '1px solid var(--border)',
        boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
      }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>
          {'⚙️'} Panel Administratora
        </h2>

        {error && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--danger-light)',
            border: '1px solid var(--danger-light)',
            borderRadius: '0.375rem',
            color: 'var(--danger)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              <i className="fas fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
              {error}
            </span>
            <button
              style={{
                background: 'none',
                
                color: 'var(--danger)',
                fontSize: '18px'
              }}
              onClick={() => setError(null)}
            >
              {'✕'}
            </button>
          </div>
        )}
      </div>

      {/* Tabs Navigation + Content */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '0.375rem',
        overflow: 'hidden',
        boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
      }}>
        {/* Tab rows */}
        {tabRows.map((row, rowIdx) => (
          <div key={rowIdx} style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: row.bg,
            borderBottom: '1px solid var(--border)',
            padding: '0.5rem'
          }}>
            <div className="admin-row-label" style={{
              minWidth: '140px',
              padding: '0.5rem 1rem',
              fontWeight: '600',
              color: 'var(--text)',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span>{row.icon}</span> {row.label}
            </div>
            <div className="admin-tab-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
              {row.tabs.map(tab => (
                <button
                  key={tab.id}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: activeTab === tab.id ? 'var(--primary)' : 'var(--card-bg)',
                    border: activeTab === tab.id ? '1px solid var(--primary)' : '1px solid var(--border)',
                    color: activeTab === tab.id ? '#fff' : 'var(--text-secondary)',
                    fontWeight: activeTab === tab.id ? '600' : '500',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Wysyłka row (special - navigation buttons) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'var(--bg)',
          borderBottom: '1px solid var(--border)',
          padding: '0.5rem'
        }}>
          <div style={{
            minWidth: '140px',
            padding: '0.5rem 1rem',
            fontWeight: '600',
            color: 'var(--text)',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>🚚</span> Wysyłka
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
            <button
              style={{
                padding: '6px 12px',
                backgroundColor: 'var(--warning)',
                color: 'var(--text)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
              onClick={() => navigate('/admin/shipping')}
            >
              <span>📦</span>
              <span>Ustawienia InPost</span>
            </button>
            <button
              style={{
                padding: '6px 12px',
                border: '1px solid var(--info)', color: 'var(--info)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
              onClick={() => navigate('/admin/shipments')}
            >
              <span>📋</span>
              <span>Lista Przesyłek</span>
            </button>
          </div>
        </div>

        {/* Wygląd row (special - navigation button) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: "var(--card-bg)",
          borderBottom: '1px solid var(--border)',
          padding: '0.5rem'
        }}>
          <div style={{
            minWidth: '140px',
            padding: '0.5rem 1rem',
            fontWeight: '600',
            color: 'var(--text)',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>🎨</span> Wygląd
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
            <button
              style={{
                padding: '6px 12px',
                border: '1px solid var(--purple)', color: 'var(--purple)',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem'
              }}
              onClick={() => navigate('/admin/theme')}
            >
              <span>🖌️</span>
              <span>Ustawienia wyglądu</span>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="admin-tab-content" style={{ padding: '1.5rem' }}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
